import type { Dispatch } from "react";

import { isExpiredCursor } from "src/components/lineage/lineage-pagination";
import {
  LineageStatus,
  type ResourceDependentsPageQuery,
  type ResourceDependentsSummaryQuery,
} from "src/graphql";

import { RESOURCE_DEPENDENTS_PAGE_SIZE } from "./resource-dependents-page-loader";
import type { ResourceTypeOption } from "./resource-display";

type ResourceDependentsPage =
  ResourceDependentsPageQuery["resourceDependentsPage"];

type ResourceDependentsPageRow = NonNullable<
  ResourceDependentsPage["rows"]
>[number];

type ResourceDependentsSummary =
  ResourceDependentsSummaryQuery["resourceDependentsSummary"];

export type UsageNode = ResourceDependentsPageRow["node"];

export type UsageRow = {
  // Unique per position in the tree, not per node. Nodes can repeat under different parents.
  key: string;
  parentKey: string | null;
  node: UsageNode;
  depth: number;
  // Exact number of resources that directly depend on this row.
  directDependentCount: number;
  isExpanded: boolean;
  // Distinguishes an unopened row from a loaded row with no rendered children.
  childrenLoaded: boolean;
  childCursor: string | null;
  isLoadingChildren: boolean;
  hasChildrenError: boolean;
};

// What a query hook last returned, which the panel renders from instead of the hook.
export type QueryRead<TData> = {
  data: TData | undefined;
  error: Error | null;
  isLoading: boolean;
};

type SummaryCounts = {
  directDependents: number;
  indirectDependents: number;
};

// The fields a new snapshot resets. The rest, like the type selection, carries over to it.
type TreeRows = Pick<
  UsageTreeState,
  | "rowsByKey"
  | "orderedKeys"
  | "rootCursor"
  | "isLoadingRootPage"
  | "hasRootPageError"
  | "hasExpired"
  | "hasTree"
>;

export type UsageTreeState = {
  // The snapshot every loaded row was read from. Pages are pinned to it, so a
  // new token means a new tree rather than more rows for this one.
  snapshotToken: string | null;
  rowsByKey: ReadonlyMap<string, UsageRow>;
  // Render order of the rows, used for controlling insertion order of new rows.
  orderedKeys: readonly string[];
  rootCursor: string | null;
  isLoadingRootPage: boolean;
  hasRootPageError: boolean;
  // The snapshot has been replaced, so its cursors no longer read.
  hasExpired: boolean;
  // Can be newer than the rows, which a first page seeds only once its snapshot is ready.
  firstPage: QueryRead<ResourceDependentsPage>;
  // Read for the snapshot the rows come from.
  summary: QueryRead<ResourceDependentsSummary>;
  selectedTypes: TypeSelection;
  // The reducer derives the fields below from the ones above, so the panel renders them as they are.
  hasTree: boolean;
  lastCalculatedAt: Date | null;
  summaryCounts: SummaryCounts | null;
  countsByType: ReadonlyMap<string, number>;
  // The trigger and the tree both read this, so they can't disagree.
  typeFilter: TypeSelection;
  // Leaves out an expired snapshot, which the panel reports on its own.
  hasSummaryError: boolean;
};

/**
 * Every action a request for rows produces names the snapshot and the cursor it
 * was issued for, so the reducer can tell a result it still wants from one the
 * tree has already moved past. A result that does not match is dropped.
 */
export type UsageTreeAction =
  | { type: "first-page-read"; read: QueryRead<ResourceDependentsPage> }
  | { type: "summary-read"; read: QueryRead<ResourceDependentsSummary> }
  | { type: "types-selected"; selectedTypes: TypeSelection }
  | { type: "root-page-requested"; cursor: string }
  | {
      type: "root-page-loaded";
      snapshotToken: string;
      cursor: string;
      page: ResourceDependentsPage;
    }
  | { type: "root-page-failed"; snapshotToken: string; cursor: string }
  | { type: "children-requested"; key: string; cursor: string | null }
  | {
      type: "children-loaded";
      snapshotToken: string;
      key: string;
      cursor: string | null;
      page: ResourceDependentsPage;
    }
  | {
      type: "children-failed";
      snapshotToken: string;
      key: string;
      cursor: string | null;
    }
  | { type: "snapshot-expired"; snapshotToken: string }
  | { type: "reload-requested" }
  | { type: "row-toggled"; key: string }
  | { type: "collapsed-all" };

// The read each query read action carries, keyed by the action's type.
type QueryReadByAction = {
  [A in Extract<UsageTreeAction, { read: unknown }> as A["type"]]: A["read"];
};

// A mapped type, so TypeScript can pair a generic action type with its own read.
type QueryReadAction<T extends keyof QueryReadByAction> = {
  [K in T]: { type: K; read: QueryReadByAction[K] };
}[T];

export function toResourceTypeCounts(
  entries: readonly { resourceType: string; count: number }[],
): ReadonlyMap<string, number> {
  return new Map(
    entries.map((entry) => [entry.resourceType, entry.count] as const),
  );
}

// Notice that we need to add the parent key to ensure uniqueness of the row key.
function rowKey(parentKey: string | null, nodeId: string): string {
  return `${parentKey ?? ""}/${nodeId}`;
}

function emptyTreeRows(): TreeRows {
  return {
    rowsByKey: new Map(),
    orderedKeys: [],
    rootCursor: null,
    isLoadingRootPage: false,
    hasRootPageError: false,
    hasExpired: false,
    hasTree: false,
  };
}

export function emptyTreeState(): UsageTreeState {
  return {
    snapshotToken: null,
    ...emptyTreeRows(),
    // Matches what the hooks return on mount, where the summary waits for a snapshot.
    firstPage: { data: undefined, error: null, isLoading: true },
    summary: { data: undefined, error: null, isLoading: false },
    selectedTypes: "all",
    lastCalculatedAt: null,
    summaryCounts: null,
    countsByType: new Map(),
    typeFilter: "all",
    hasSummaryError: false,
  };
}

// react-query keeps the data and error it returns as the same objects until they change.
// read.data must be part of the query's data rather than a copy, or every render would dispatch.
export function dispatchIfDifferentRead<T extends keyof QueryReadByAction>(
  dispatch: Dispatch<QueryReadAction<T>>,
  type: T,
  read: QueryReadByAction[T],
  current: QueryReadByAction[T],
): void {
  if (
    read.data !== current.data ||
    read.error !== current.error ||
    read.isLoading !== current.isLoading
  ) {
    dispatch({ type, read });
  }
}

// The cursor a page carries for its continuation, or null once it is the last page.
function nextCursor(page: ResourceDependentsPage): string | null {
  return page.pageInfo.hasNextPage ? (page.pageInfo.endCursor ?? null) : null;
}

// The cursor a request for this row's next children must have. An unopened row
// has no cursor, so its first request doesn't have one.
function getChildCursor(row: UsageRow): string | null {
  return row.childrenLoaded ? row.childCursor : null;
}

function isCurrentSnapshot(
  state: UsageTreeState,
  snapshotToken: string,
): boolean {
  return !state.hasExpired && state.snapshotToken === snapshotToken;
}

/**
 * Adds a fetched page to the tree. Root rows go at the end and child rows go after
 * their parent's existing descendants. Existing rows are skipped so retries do
 * not create duplicates.
 */
function appendRows({
  state,
  parentKey,
  rows,
}: {
  state: UsageTreeState;
  parentKey: string | null;
  rows: readonly ResourceDependentsPageRow[];
}): UsageTreeState {
  const rowsByKey = new Map(state.rowsByKey);
  const parent = parentKey === null ? null : rowsByKey.get(parentKey);
  const depth = parent ? parent.depth + 1 : 0;
  const added: string[] = [];

  for (const row of rows) {
    const node = row.node;
    const key = rowKey(parentKey, node.id);
    if (rowsByKey.has(key)) {
      continue;
    }
    rowsByKey.set(key, {
      key,
      parentKey,
      node,
      depth,
      directDependentCount: row.directDependentCount,
      isExpanded: false,
      childrenLoaded: false,
      childCursor: null,
      isLoadingChildren: false,
      hasChildrenError: false,
    });
    added.push(key);
  }

  const orderedKeys = insertAfterSubtree(state.orderedKeys, parentKey, added);
  return {
    ...state,
    rowsByKey,
    orderedKeys,
    // Rows only arrive through here, so hasTree can't fall behind them.
    hasTree: orderedKeys.length > 0,
  };
}

/**
 * Appends new child rows after the parent's existing descendants and before
 * the parent's next sibling.
 */
function insertAfterSubtree(
  orderedKeys: readonly string[],
  parentKey: string | null,
  added: readonly string[],
): string[] {
  if (added.length === 0) {
    return [...orderedKeys];
  }
  if (parentKey === null) {
    return [...orderedKeys, ...added];
  }
  const prefix = `${parentKey}/`;
  let insertAt = orderedKeys.indexOf(parentKey);
  if (insertAt === -1) {
    return [...orderedKeys, ...added];
  }
  insertAt += 1;
  while (insertAt < orderedKeys.length) {
    const key = orderedKeys[insertAt];
    if (key === undefined || !key.startsWith(prefix)) {
      break;
    }
    insertAt += 1;
  }
  return [
    ...orderedKeys.slice(0, insertAt),
    ...added,
    ...orderedKeys.slice(insertAt),
  ];
}

function patchRow(
  state: UsageTreeState,
  key: string,
  patch: Partial<UsageRow>,
): UsageTreeState {
  const row = state.rowsByKey.get(key);
  if (!row) {
    return state;
  }
  const rowsByKey = new Map(state.rowsByKey);
  rowsByKey.set(key, { ...row, ...patch });
  return { ...state, rowsByKey };
}

// The row this action addresses, while the action still matches what the row is
// waiting for.
function pendingRow(
  state: UsageTreeState,
  action: { snapshotToken: string; key: string; cursor: string | null },
): UsageRow | null {
  if (!isCurrentSnapshot(state, action.snapshotToken)) {
    return null;
  }
  const row = state.rowsByKey.get(action.key);
  if (!row || getChildCursor(row) !== action.cursor) {
    return null;
  }
  return row;
}

function stopAllLoading(state: UsageTreeState): UsageTreeState {
  const rowsByKey = new Map(state.rowsByKey);
  for (const [key, row] of rowsByKey) {
    if (row.isLoadingChildren) {
      rowsByKey.set(key, { ...row, isLoadingChildren: false });
    }
  }
  return { ...state, rowsByKey, isLoadingRootPage: false };
}

// This determines when the graph was last generated. It's pulled from the first page.
function getLastCalculatedAt(
  page: ResourceDependentsPage | undefined,
): Date | null {
  const completedAt = (page?.partitions ?? [])
    .map((partition) => partition.snapshotCompletedAt)
    .filter((value): value is string => Boolean(value))
    .map((value) => new Date(value).getTime());
  return completedAt.length > 0 ? new Date(Math.max(...completedAt)) : null;
}

function getSummaryCounts(
  summary: ResourceDependentsSummary | undefined,
): SummaryCounts | null {
  // Zero is a count the summary shows, so the guard is for a count the read
  // could not produce rather than for a falsy one.
  return summary?.directDependents != null &&
    summary?.indirectDependents != null
    ? {
        directDependents: summary.directDependents,
        indirectDependents: summary.indirectDependents,
      }
    : null;
}

// A selection that resolves to every type is stored as "all", so types a newer summary adds start selected.
function resolveTypeSelection(
  selectedTypes: TypeSelection,
  countsByType: ReadonlyMap<string, number>,
): Pick<UsageTreeState, "selectedTypes" | "typeFilter"> {
  const typeFilter = resolveTypeFilter(selectedTypes, countsByType);
  return {
    selectedTypes: typeFilter === "all" ? "all" : selectedTypes,
    typeFilter,
  };
}

export function usageTreeReducer(
  state: UsageTreeState,
  action: UsageTreeAction,
): UsageTreeState {
  switch (action.type) {
    case "first-page-read": {
      const page = action.read.data;
      const next = {
        ...state,
        firstPage: action.read,
        lastCalculatedAt:
          page === state.firstPage.data
            ? state.lastCalculatedAt
            : getLastCalculatedAt(page),
      };
      // Only a ready page seeds the tree, so a rebuild keeps the last ready snapshot on screen.
      // A page from the snapshot the tree already shows leaves its rows as they are.
      if (
        page?.status !== LineageStatus.Ready ||
        !page.snapshotToken ||
        page.snapshotToken === state.snapshotToken
      ) {
        return next;
      }
      const seeded = appendRows({
        state: {
          ...next,
          ...emptyTreeRows(),
          snapshotToken: page.snapshotToken,
        },
        parentKey: null,
        rows: page.rows ?? [],
      });
      return { ...seeded, rootCursor: nextCursor(page) };
    }

    case "summary-read": {
      const summary = action.read.data;
      const next = {
        ...state,
        summary: action.read,
        hasSummaryError:
          Boolean(action.read.error) && !isExpiredCursor(action.read.error),
      };
      if (summary === state.summary.data) {
        return next;
      }
      const countsByType = toResourceTypeCounts(
        summary?.dependentCountsByType ?? [],
      );
      return {
        ...next,
        summaryCounts: getSummaryCounts(summary),
        countsByType,
        ...resolveTypeSelection(state.selectedTypes, countsByType),
      };
    }

    case "types-selected": {
      if (action.selectedTypes === state.selectedTypes) {
        return state;
      }
      return {
        ...state,
        ...resolveTypeSelection(action.selectedTypes, state.countsByType),
      };
    }

    case "root-page-requested": {
      if (state.hasExpired || state.rootCursor !== action.cursor) {
        return state;
      }
      return { ...state, isLoadingRootPage: true, hasRootPageError: false };
    }

    case "root-page-loaded": {
      if (
        !isCurrentSnapshot(state, action.snapshotToken) ||
        state.rootCursor !== action.cursor
      ) {
        return state;
      }
      const merged = appendRows({
        state,
        parentKey: null,
        rows: action.page.rows ?? [],
      });
      return {
        ...merged,
        rootCursor: nextCursor(action.page),
        isLoadingRootPage: false,
        hasRootPageError: false,
      };
    }

    case "root-page-failed": {
      if (
        !isCurrentSnapshot(state, action.snapshotToken) ||
        state.rootCursor !== action.cursor
      ) {
        return state;
      }
      return { ...state, isLoadingRootPage: false, hasRootPageError: true };
    }

    case "children-requested": {
      const row = state.rowsByKey.get(action.key);
      if (state.hasExpired || !row || getChildCursor(row) !== action.cursor) {
        return state;
      }
      return patchRow(state, action.key, {
        isLoadingChildren: true,
        hasChildrenError: false,
      });
    }

    case "children-loaded": {
      if (!pendingRow(state, action)) {
        return state;
      }
      const merged = appendRows({
        state,
        parentKey: action.key,
        rows: action.page.rows ?? [],
      });
      return patchRow(merged, action.key, {
        childrenLoaded: true,
        isLoadingChildren: false,
        hasChildrenError: false,
        childCursor: nextCursor(action.page),
      });
    }

    case "children-failed": {
      if (!pendingRow(state, action)) {
        return state;
      }
      return patchRow(state, action.key, {
        isLoadingChildren: false,
        hasChildrenError: true,
      });
    }

    case "snapshot-expired": {
      if (!isCurrentSnapshot(state, action.snapshotToken)) {
        return state;
      }
      return stopAllLoading({ ...state, hasExpired: true });
    }

    case "reload-requested": {
      // Clears the expiry so the refetch can load again. A refetch that returns
      // a new snapshot rebuilds the tree, and one that returns the same
      // snapshot means the rows are still readable.
      return state.hasExpired ? { ...state, hasExpired: false } : state;
    }

    case "row-toggled": {
      const row = state.rowsByKey.get(action.key);
      if (!row) {
        return state;
      }
      return patchRow(state, action.key, { isExpanded: !row.isExpanded });
    }

    case "collapsed-all": {
      let didChange = false;
      const rowsByKey = new Map(state.rowsByKey);
      for (const [key, row] of rowsByKey) {
        if (row.isExpanded) {
          rowsByKey.set(key, { ...row, isExpanded: false });
          didChange = true;
        }
      }
      return didChange ? { ...state, rowsByKey } : state;
    }
  }
}

export function hasExpandedRows(state: UsageTreeState): boolean {
  for (const row of state.rowsByKey.values()) {
    if (row.isExpanded) {
      return true;
    }
  }
  return false;
}

export function hasExpandableRows(state: UsageTreeState): boolean {
  for (const row of state.rowsByKey.values()) {
    if (row.directDependentCount > 0) {
      return true;
    }
  }
  return false;
}

export function countLoadedChildren(
  state: UsageTreeState,
): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const row of state.rowsByKey.values()) {
    if (row.parentKey !== null) {
      counts.set(row.parentKey, (counts.get(row.parentKey) ?? 0) + 1);
    }
  }
  return counts;
}

export function pendingChildRowCount(
  row: UsageRow,
  loadedChildCount: number,
): number {
  const remaining = row.directDependentCount - loadedChildCount;
  return Math.max(1, Math.min(remaining, RESOURCE_DEPENDENTS_PAGE_SIZE));
}

// A row is hidden while any ancestor is collapsed.
function isRevealed(
  state: UsageTreeState,
  row: UsageRow,
  forcedOpenKeys: ReadonlySet<string>,
): boolean {
  let parentKey = row.parentKey;
  while (parentKey !== null) {
    const parent = state.rowsByKey.get(parentKey);
    if (!parent) {
      return false;
    }
    if (!parent.isExpanded && !forcedOpenKeys.has(parent.key)) {
      return false;
    }
    parentKey = parent.parentKey;
  }
  return true;
}

// "all" selects every type, including types a newer summary adds, and an empty set selects none.
export type TypeSelection = ReadonlySet<string> | "all";

export type UsageTreeFilter = {
  types: TypeSelection;
  search: string;
};

export function isFilteringTree(filter: UsageTreeFilter): boolean {
  return filter.types !== "all" || filter.search.trim().length > 0;
}

export function resolveTypeFilter(
  selected: TypeSelection,
  countsByType: ReadonlyMap<string, number>,
): TypeSelection {
  // Until the summary loads, a selection stands as is, so a subset never flashes unfiltered during a refetch.
  if (selected === "all" || selected.size === 0 || countsByType.size === 0) {
    return selected;
  }
  const counted = [...countsByType]
    .filter(([, count]) => count > 0)
    .map(([type]) => type);
  const kept = counted.filter((type) => selected.has(type));
  // Keeping every counted type filters nothing, so a trigger that reads "All" never leaves the tree filtered.
  // Keeping none means a newer summary dropped every selected type, so the tree falls back to all of them.
  if (kept.length === 0 || kept.length === counted.length) {
    return "all";
  }
  return new Set(kept);
}

// A selection of every option is stored as "all", so types a newer summary adds start selected.
export function toTypeSelection(
  next: ReadonlySet<string>,
  options: readonly Pick<ResourceTypeOption, "value">[],
): TypeSelection {
  return options.every((option) => next.has(option.value)) ? "all" : next;
}

// Filters loaded rows.
// TODO: search cannot reach descendants not yet paged in.
export function filterTree(
  state: UsageTreeState,
  filter: UsageTreeFilter,
): { visibleKeys: ReadonlySet<string>; forcedOpenKeys: ReadonlySet<string> } {
  const search = filter.search.trim().toLocaleLowerCase();
  const { types } = filter;

  const matches = (row: UsageRow): boolean => {
    if (types !== "all" && !types.has(row.node.resourceType)) {
      return false;
    }
    if (search.length > 0) {
      return (row.node.name ?? "").toLocaleLowerCase().includes(search);
    }
    return true;
  };

  if (!isFilteringTree(filter)) {
    const visibleKeys = new Set<string>();
    for (const key of state.orderedKeys) {
      const row = state.rowsByKey.get(key);
      if (row && isRevealed(state, row, new Set())) {
        visibleKeys.add(key);
      }
    }
    return { visibleKeys, forcedOpenKeys: new Set() };
  }

  // Keep and expand a matching row's ancestors so the match remains visible.
  const visibleKeys = new Set<string>();
  const forcedOpenKeys = new Set<string>();
  for (const key of state.orderedKeys) {
    const row = state.rowsByKey.get(key);
    if (!row || !matches(row)) {
      continue;
    }
    visibleKeys.add(key);
    let parentKey = row.parentKey;
    while (parentKey) {
      const parent = state.rowsByKey.get(parentKey);
      if (!parent) {
        break;
      }
      visibleKeys.add(parent.key);
      forcedOpenKeys.add(parent.key);
      parentKey = parent.parentKey;
    }
  }
  return { visibleKeys, forcedOpenKeys };
}

// The rows to render, in order, after expansion and filtering.
export function visibleRows(
  state: UsageTreeState,
  filter: UsageTreeFilter,
): { rows: UsageRow[]; forcedOpenKeys: ReadonlySet<string> } {
  const { visibleKeys, forcedOpenKeys } = filterTree(state, filter);
  const rows: UsageRow[] = [];
  for (const key of state.orderedKeys) {
    if (!visibleKeys.has(key)) {
      continue;
    }
    const row = state.rowsByKey.get(key);
    if (row && isRevealed(state, row, forcedOpenKeys)) {
      rows.push(row);
    }
  }
  return { rows, forcedOpenKeys };
}
